import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import { serverMessage } from "../../../services/professionals";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

// Confirms removing one entry — an email, a personal phone, an address — and
// reports back what happened.
//
// The removal itself is the caller's: this only asks, runs it and shows the
// failure. Where the server rejects with a reason of its own ("The last personal
// phone cannot be removed"), that reason is shown rather than a generic message,
// since it says exactly which rule was hit.

interface Props {
  open: boolean;
  title: string;
  /** What is about to be removed, shown so the confirmation is specific. */
  subject: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function ConfirmRemoveDialog({
  open,
  title,
  subject,
  onClose,
  onConfirm
}: Props) {
  const { t } = useTranslation();
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh dialog never opens showing the previous attempt's failure.
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setRemoving(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(serverMessage(err) ?? t("contactLists.removeError"));
    } finally {
      setRemoving(false);
    }
  }, [onConfirm, onClose, t]);

  return (
    <DialogComponent open={open} handleModal={onClose} headerTxt={title}>
      <DialogContent>
        <Typography
          sx={{ ...typography.body.m, color: semanticColors.text.secondary }}
        >
          {t("contactLists.removeConfirm")}
        </Typography>
        <Typography
          sx={{
            ...typography.body.lSemibold,
            color: semanticColors.text.primary,
            mt: 1,
            mb: 2,
            wordBreak: "break-word"
          }}
        >
          {subject}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("contactLists.remove")}
            onClick={handleConfirm}
            loading={removing}
            variant="danger"
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
